import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnInit, Type} from '@angular/core';
import {SafeHtml} from '@angular/platform-browser';
import {ActivatedRouteSnapshot, NavigationEnd, Router} from '@angular/router';
import {Observable} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';
import appConfig from '../../config/app-config';
import {UserRole} from '../../enums/user-role';
import {ChangePasswordPageComponent} from '../../pages/change-password-page';
import {ConfirmSignUpPageComponent} from '../../pages/confirm-sign-up-page';
import {LoginTwoFAWrapperPageComponent} from '../../pages/login-two-f-a-wrapper-page';
import {ResetPasswordPageComponent} from '../../pages/reset-password-page';
import {SignUpPageComponent} from '../../pages/sign-up-page';
import {WidgetPageComponent} from '../../pages/widget-page';
import {HtmlSymbolService} from '../../services/html-symbol.service';
import {UserService} from '../../services/user.service';
import {AuthService} from '../auth/auth.service';
import {destroy$} from '../destroyable';
import {VersionService} from '../version/version.service';
import {resolveLogoImgUrl} from './logo-img-utils';

export interface Logo {
    img: string;
    title: string;
    routerLink: any[] | string;
}

const APP_HEADER_LOGO_BY_USER_ROLE: { [userRole in UserRole]: Logo } = {
    TRADER:       {
        img:        resolveLogoImgUrl('logoImageUrl'),
        title:      appConfig.applicationTitle,
        routerLink: ['/']
    },
    ADMIN:        {
        img:        resolveLogoImgUrl('backofficeLogoImageUrl', 'logoImageUrl'),
        title:      `${appConfig.applicationTitle} Backoffice`,
        routerLink: ['/']
    },
    TRADER_ADMIN: {
        img:        resolveLogoImgUrl('logoImageUrl'),
        title:      appConfig.applicationTitle,
        routerLink: ['/']
    }
};
const APP_HEADER_DEFAULT_LOGO = APP_HEADER_LOGO_BY_USER_ROLE.TRADER;

@Component({
    selector: 'app-header',
    templateUrl: './app-header.component.html',
    styleUrls: ['./app-header.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppHeaderComponent implements OnInit {

    public hideOnPageComponents: Array<Type<any>> = [
        LoginTwoFAWrapperPageComponent,
        WidgetPageComponent,
        SignUpPageComponent,
        ConfirmSignUpPageComponent,
        ResetPasswordPageComponent,
        ChangePasswordPageComponent
    ];

    public logo: Logo = APP_HEADER_DEFAULT_LOGO;

    public isVisible: boolean = false;
    public readonly destroy$ = destroy$();

    public displayUsername$: Observable<string | SafeHtml> = this.userService.user$.pipe(
        map(user => user && user.login || this.htmlSymbolService.mdash())
    );

    public isGuest: boolean = false;
    public isAdmin: boolean = false;

    public readonly buildInfoText = inject(VersionService).getBuildInfoText();

    constructor(
        public elementRef: ElementRef,
        private router: Router,
        private authService: AuthService,
        private userService: UserService,
        private htmlSymbolService: HtmlSymbolService,
        private changeDetectorRef: ChangeDetectorRef
    ) {}

    public ngOnInit(): void {
        this.updateForCurrentRoute();
        this.listenToRoute();
        this.listenToUserRole();
    }

    private listenToRoute(): void {
        this.router.events.pipe(
            takeUntil(this.destroy$)
        ).subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.updateForCurrentRoute();
            }
        });
    }

    private listenToUserRole(): void {
        this.authService.userRole$.pipe(
            tap(userRole => {
                this.isGuest = !userRole;
                this.isAdmin = userRole === UserRole.ADMIN;

                this.logo = userRole
                    ? APP_HEADER_LOGO_BY_USER_ROLE[userRole] || APP_HEADER_DEFAULT_LOGO
                    : APP_HEADER_DEFAULT_LOGO;

                this.changeDetectorRef.markForCheck();
            })
        ).subscribe();
    }

    private updateForCurrentRoute(): void {
        const currentRouteSnapshot: ActivatedRouteSnapshot = this.router.routerState.snapshot.root.firstChild;

        if (currentRouteSnapshot) { // It may be not defined at startup
            const pageComponent: any = currentRouteSnapshot.component;

            this.isVisible = (this.hideOnPageComponents || []).indexOf(pageComponent) < 0;
        }
    }

}
